import React from 'react';
import { BUTTON_STYLES } from '../../constants/styles';
import { ValidationWarning } from '../Messages/StatusMessages';
import { groupDatesByWeek } from '../../utils/weekUtils';

const ProteinSubstitutionSection = ({
    order,
    orderIndex,
    onProteinSubstitution,
    isLocked,
    saving,
    billingChanges,
    deliveryDates,
    selectedDeliveryDate
}) => {
    const proteinData = order.proteinOptions;

    if (!proteinData) {
        return null;
    }

    // Calculate whether to show premium pricing based on pre-calculated billing data
    const shouldShowPremiumPricing = (proteinOption) => {
        const hasUpgradePrice = proteinOption.price != null && Number(proteinOption.price) > 0;

        if (!hasUpgradePrice) {
            return false; // No premium price to show
        }

        // If we don't have billing data, show the price (safe default)
        if (!billingChanges?.premiumUsageByWeek || !deliveryDates || !selectedDeliveryDate) {
            console.log('⚠️ Missing billing data - showing price by default');
            return true;
        }

        const mealType = order.meal;

        // Find which week this delivery date belongs to
        const datesByWeek = groupDatesByWeek(deliveryDates);
        let weekKey = null;
        for (const [key, datesInWeek] of Object.entries(datesByWeek)) {
            if (datesInWeek.includes(selectedDeliveryDate)) {
                weekKey = key;
                break;
            }
        }

        if (!weekKey) {
            console.log('⚠️ Could not find week for date:', selectedDeliveryDate);
            return true; // Safe default if we can't find week
        }

        // Check if current protein is premium (if so, we're replacing, not adding)
        const currentActiveProtein = proteinData.find(opt => opt.isActive);
        const currentProteinPrice = currentActiveProtein?.price || 0;
        const isReplacingPremium = currentProteinPrice > 0;

        // If replacing premium with premium, no net change - don't show price
        if (isReplacingPremium && proteinOption.id !== currentActiveProtein?.id) {
            return false;
        }

        // Use pre-calculated premium usage data
        const premiumUsage = billingChanges.premiumUsageByWeek[weekKey]?.[mealType];
        if (!premiumUsage) {
            console.log('⚠️ No premium usage data for', weekKey, mealType);
            return true; // Safe default if no data
        }

        // If selecting a different non-active premium option and we've exceeded subscription
        if (!proteinOption.isActive && !isReplacingPremium && premiumUsage.hasExceeded) {
            return true; // Show price - customer has used up their premium subscription plan
        }

        // Otherwise don't show price
        return false;
    };

    const getActiveProteinCount = () => {
        if (!proteinData) return null;
        return proteinData.filter(option => option.isActive).length;
    };

    return (
        <div style={{ marginBottom: '15px' }}>
            <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: 'bold',
                color: '#555'
            }}>
                Protein:
            </label>
            {getActiveProteinCount() !== null && getActiveProteinCount() !== undefined && getActiveProteinCount() < 1 && (
                <ValidationWarning message="No Protein Selected" />
            )}
            <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px'
            }}>
                {proteinData.map((proteinOption, index) => {
                    const isActive = proteinOption.isActive;
                    const isDisabled = isLocked || saving || isActive;
                    const showPrice = shouldShowPremiumPricing(proteinOption);
                    return (
                        <button
                            key={index}
                            onClick={() => {
                                if (!isActive && onProteinSubstitution) {
                                    const currentActiveOption = proteinData.find(opt => opt.isActive);
                                    const oldProteinId = currentActiveOption ? currentActiveOption.id : null;
                                    onProteinSubstitution(orderIndex, proteinOption.id, oldProteinId);
                                }
                            }}
                            disabled={isDisabled}
                            style={BUTTON_STYLES.substitution(isActive, isDisabled)}
                            title={isActive ? `Current protein: ${proteinOption.variantName || proteinOption.name}` : `Switch to ${proteinOption.variantName || proteinOption.name}`}
                        >
                            <span style={{
                                marginRight: '4px',
                                fontSize: '12px'
                            }}>
                                {isActive ? '✓' : '+'}
                            </span>
                            {proteinOption.variantName || proteinOption.name}
                            {showPrice && (
                                <span style={{
                                    marginLeft: '4px',
                                    fontSize: '11px',
                                    fontWeight: 'bold',
                                    color: '#856404'
                                }}>
                                    (+${proteinOption.price})
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default ProteinSubstitutionSection;